import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { PaymentMethod } from './paymentApi';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
}));

const brandLabels: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

interface PaymentMethodCardProps {
  method: PaymentMethod;
  onRemove: (pmId: string) => void;
  onSetDefault: (pmId: string) => void;
  isRemoving: boolean;
  isSettingDefault: boolean;
}

const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  method,
  onRemove,
  onSetDefault,
  isRemoving,
  isSettingDefault,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const brandLabel = brandLabels[method.brand] || method.brand;
  const expiry = `${String(method.expMonth).padStart(2, '0')}/${String(
    method.expYear
  ).slice(-2)}`;

  return (
    <>
      <StyledCard>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {brandLabel}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                •••• {method.last4}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expires {expiry}
              </Typography>
              {method.isDefault && (
                <Chip
                  label="Default"
                  size="small"
                  color="primary"
                  variant="filled"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!method.isDefault && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isSettingDefault}
                  onClick={() => onSetDefault(method.id)}
                >
                  Set as Default
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={isRemoving}
                onClick={() => setConfirmOpen(true)}
              >
                Remove
              </Button>
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => onRemove(method.id)}
        title={`Remove ${brandLabel} ending in ${method.last4}?`}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </>
  );
};

export default PaymentMethodCard;
